import { useEffect, useState } from "react";
import {
  suggestMetrics,
  useComputedMetrics,
  useSaveScoreboardConfig,
  useScoreboardConfig,
  type ComputedMetric,
  type MetricSpec,
} from "../../hooks/useScoreboard";

/**
 * ScoreboardConfigurator — describe the evidence signal you care about; the
 * AI proposes 3-4 quantifiable metrics, each previewed with REAL numbers
 * (computed server-side). Keep/drop, then save. Saving an empty set restores
 * the default scoreboard.
 */
export default function ScoreboardConfigurator({
  caseId,
  open,
  onClose,
}: {
  caseId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data: savedSpecs } = useScoreboardConfig(caseId);
  const { data: savedComputed } = useComputedMetrics(caseId, savedSpecs);
  const save = useSaveScoreboardConfig(caseId);

  const [interest, setInterest] = useState("");
  const [metrics, setMetrics] = useState<ComputedMetric[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Seed the working set from the saved config when the modal opens.
  useEffect(() => {
    if (open) setMetrics(savedComputed ?? []);
  }, [open, savedComputed]);

  if (!open) return null;

  async function onSuggest() {
    setBusy(true);
    setErr(null);
    try {
      const result = await suggestMetrics(caseId, interest);
      if (result.length === 0) {
        setErr("No measurable metrics matched that. Try naming a person, a topic, or a date.");
      } else {
        setMetrics(result);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function remove(i: number) {
    setMetrics((m) => m.filter((_, idx) => idx !== i));
  }

  async function onSave() {
    const specs: MetricSpec[] = metrics.map((m) => ({
      primitive: m.primitive,
      label: m.label,
      params: m.params ?? {},
    }));
    await save.mutateAsync(specs);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded border border-steel-dark bg-iron/95 shadow-2xl">
        <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
          <span className="text-bone-dim">// configure the scoreboard</span>
          <button type="button" onClick={onClose} className="text-bone-mute hover:text-bone" aria-label="close">✕</button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
              // what evidence signal matters to you?
            </label>
            <textarea
              value={interest}
              onChange={(e) => setInterest(e.target.value)}
              rows={3}
              placeholder="e.g. how often Kaylan raises the trust accounts, and whether she went quiet after the audit"
              className="mt-2 w-full resize-none border border-steel-dark bg-black/40 px-3 py-2 font-display text-sm text-bone outline-none transition-colors focus:border-ember-bright/60"
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute/70">
                numbers are computed from your real corpus — never guessed
              </p>
              <button
                type="button"
                onClick={onSuggest}
                disabled={busy || !interest.trim()}
                className="rounded border border-ember-bright/60 bg-ember/20 px-4 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/40 disabled:opacity-50"
              >
                {busy ? "thinking…" : "⊹ suggest metrics"}
              </button>
            </div>
            {err && <p className="mt-2 font-display text-base text-ember-bright">// {err}</p>}
          </div>

          {metrics.length > 0 && (
            <div className="space-y-2">
              <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
                // proposed // {metrics.length}
              </p>
              {metrics.map((m, i) => (
                <div key={`${m.primitive}-${i}`} className="flex items-start gap-3 rounded border border-steel-dark/70 bg-black/30 px-4 py-3">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-2xl font-light text-ember-bright">
                        {m.value ?? "—"}
                      </span>
                      <span className="font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
                        {m.unit}
                        {m.pct != null ? ` · ${m.pct}%` : ""}
                      </span>
                    </div>
                    <p className="mt-0.5 font-display text-base font-light text-bone">{m.label}</p>
                    {m.why && <p className="mt-1 font-display text-sm leading-snug text-bone-dim">{m.why}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="shrink-0 font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute hover:text-ember-bright"
                    aria-label="remove metric"
                  >
                    drop
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-steel-dark/70 px-5 py-3">
          <button
            type="button"
            onClick={() => setMetrics([])}
            className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute hover:text-bone"
          >
            clear // use default
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={save.isPending}
            className="rounded border border-ember-bright/60 bg-ember/30 px-5 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/50 disabled:opacity-50"
          >
            {save.isPending ? "saving…" : "save scoreboard →"}
          </button>
        </div>
      </div>
    </div>
  );
}
